import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import environment from '@environments/environment';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Observable } from 'rxjs';
import { IResults } from '../interfaces/helper.interface';
import {
  ICIRWorkspaceCreate,
  IrisWorkspace,
  IStudy,
  IStudyAdditionalParams,
  SiteSearchCallbacks,
} from '@data-access/setup';
@Injectable({
  providedIn: 'root',
})
export class CirSetupService {
  private http = inject(HttpClient);
  private siteSearchCallbacks = signal<SiteSearchCallbacks>({
    byRegion: (study, field, pageNumber, pageSize) =>
      this.getSitesForStudyByRegion(study, pageNumber, pageSize, field),
    default: (study, field, pageNumber, pageSize) =>
      this.getSitesStudySearch(study, pageNumber, pageSize, field),
    singleSite: (study, field, pageNumber, pageSize) =>
      this.getSitesStudySearch(study, pageNumber, pageSize, field),
  });

  updateCallback(
    type: keyof SiteSearchCallbacks,
    newCallback: SiteSearchCallbacks[typeof type],
  ) {
    this.siteSearchCallbacks.update((callbacks) => ({
      ...callbacks,
      [type]: newCallback,
    }));
  }

  getCallback(type: keyof SiteSearchCallbacks) {
    return this.siteSearchCallbacks()[type];
  }

  getStudies(
    page: number,
    pageSize: number,
    options: IStudyAdditionalParams,
  ): Observable<IResults<IStudy>> {
    const paramsObj: { [key: string]: string | string[] } = {
      page_size: pageSize.toString(),
      page: page.toString(),
      grid_view: 'false',
      typeId: options.typeId,
    };

    if (options?.query) {
      paramsObj.query = options.query;
    }

    if (options?.entityTypes && options.entityTypes.length > 0) {
      paramsObj.entity_type = options.entityTypes.join(',');
    }

    const params = new HttpParams({ fromObject: paramsObj });
    return this.http.get<IResults<IStudy>>(`${environment.irisApi}/study/`, {
      params,
    });
  }

  //mtr_selection setted as true to exclude closed and draft sites
  getSitesStudySearch(
    studyId: string,
    pageNumber: number,
    page_size: number,
    field: string,
  ) {
    return this.http.get<any>(
      `${environment.irisApi}/study/${studyId}/site/?query=${field}&page=${pageNumber}&page_size=${page_size}&mtr_selection=true`,
    );
  }
  //mtr_selection setted as true to exclude closed and draft sites
  getSitesForStudyByRegion(
    studyId: string,
    pageNumber: number,
    pageSize: number,
    query?: string,
    regionFilter = 'both',
  ) {
    const queryParam = query ? `&query=${query}` : '';
    const regionParam = regionFilter ? `&region_filter=${regionFilter}` : '';

    return this.http.get<any>(
      `${environment.irisApi}/study/${studyId}/region_site/?page=${pageNumber}&page_size=${pageSize}${queryParam}${regionParam}&mtr_selection=true`,
    );
  }

  /**
   * Creates a workspace by calling V1 or V2 create endpoint, depending on
   * configured envirionment variable
   */
  createWorkspace(workspace: ICIRWorkspaceCreate) {
    const newVersion = coerceBooleanProperty(environment.workspaceNewVersion);
    if (newVersion) {
      return this.createWorkspaceV2(workspace);
    }
    return this.createWorkspaceV1(workspace);
  }

  /** Creates a workspace by calling V1 create endpoint */
  private createWorkspaceV1(workspace: ICIRWorkspaceCreate) {
    return this.http.post<IrisWorkspace>(
      `${environment.irisApi}/workspace/`,
      workspace,
    );
  }

  /** Creates a workspace by calling V2 create endpoint */
  private createWorkspaceV2(workspace: ICIRWorkspaceCreate) {
    return this.http.post<IrisWorkspace>(
      `${environment.irisApi}/v2/workspace/`,
      workspace,
    );
  }
}
